import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { LeagueCreationData, LeagueFormState } from '../../types/league';
import BasicInfoStep from './steps/BasicInfoStep';
import RulesStep from './steps/RulesStep';
import PackagesStep from './steps/PackagesStep';
import PointSystemStep from './steps/PointSystemStep';
import SquadsStep from './steps/SquadsStep';
import LeaderboardStep from './steps/LeaderboardStep';
import AwardsStep from './steps/AwardsStep';
import SideEventsStep from './steps/SideEventsStep';
import ReviewStep from './steps/ReviewStep';
const BACKEND_URL = process.env.REACT_APP_API_URL;

const CreateLeague: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const { user } = useAuth();
  const [formState, setFormState] = useState<LeagueFormState>({
    currentStep: 1,
    totalSteps: 9,
    data: {
      packages: [],
      squads: [],
      awards: [],
      sideEvents: []
    },
    errors: [],
    isSubmitting: false
  });

  // Redirect if not admin
  if (!user || user.type !== 'admin') {
    navigate('/admin/dashboard');
    return null;
  }

  const updateFormData = (stepData: Partial<LeagueCreationData>) => {
    setFormState(prev => ({
      ...prev,
      data: { ...prev.data, ...stepData },
      errors: [] // Clear errors when data changes
    }));
  };

  const addError = (field: string, message: string) => {
    setFormState(prev => ({
      ...prev,
      errors: [...prev.errors.filter(e => e.field !== field), { field, message }]
    }));
  };

  const clearErrors = () => {
    setFormState(prev => ({ ...prev, errors: [] }));
  };

  const nextStep = () => {
    if (formState.currentStep < formState.totalSteps) {
      setFormState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    }
  };

  const prevStep = () => {
    if (formState.currentStep > 1) {
      setFormState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= formState.totalSteps) {
      setFormState(prev => ({ ...prev, currentStep: step }));
    }
  };

  const submitLeague = async () => {
    setFormState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const fullUrl = `${apiUrl}/leagues`;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formState.data)
      });

      if (response.ok) {
        await response.json(); // Process response but don't need to store
        navigate('/admin/dashboard', {
          state: {
            message: `League "${formState.data.name}" created successfully!`
          }
        });
      } else {
        const errorText = await response.text();
        let error: { errors?: any[]; message?: string };
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { message: errorText || `Server error: ${response.status}` };
        }

        setFormState(prev => ({
          ...prev,
          errors: error.errors || [{ field: 'general', message: error.message || 'Unknown error occurred' }]
        }));
      }
    } catch (error) {
      setFormState(prev => ({
        ...prev,
        errors: [{ field: 'general', message: error instanceof Error ? error.message : 'Failed to create league. Please try again.' }]
      }));
    } finally {
      setFormState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const renderCurrentStep = () => {
    const stepProps = {
      data: formState.data,
      updateData: updateFormData,
      errors: formState.errors,
      addError,
      clearErrors
    };

    switch (formState.currentStep) {
      case 1:
        return <BasicInfoStep {...stepProps} />;
      case 2:
        return <RulesStep {...stepProps} />;
      case 3:
        return <PointSystemStep {...stepProps} />;
      case 4:
        return <SquadsStep {...stepProps} />;
      case 5:
        return <PackagesStep {...stepProps} />;
      case 6:
        return <LeaderboardStep {...stepProps} />;
      case 7:
        return <AwardsStep {...stepProps} />;
      case 8:
        return <SideEventsStep {...stepProps} />;
      case 9:
        return <ReviewStep {...stepProps} />;
      default:
        return <BasicInfoStep {...stepProps} />;
    }
  };

  const stepTitles = [
    t('admin.createLeague.steps.basicInfo'),
    t('admin.createLeague.steps.rules'),
    t('admin.createLeague.steps.pointSystem'),
    t('admin.createLeague.steps.squads'),
    t('admin.createLeague.steps.packages'),
    t('admin.createLeague.steps.leaderboard'),
    t('admin.createLeague.steps.awards'),
    t('admin.createLeague.steps.sideEvents'),
    t('admin.createLeague.steps.review')
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('admin.createLeague.title')}</h1>
              <p className="text-purple-200 mt-1">
                {t('admin.createLeague.stepOf', { current: formState.currentStep, total: formState.totalSteps })}: {stepTitles[formState.currentStep - 1]}
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200"
            >
              {t('admin.createLeague.cancel')}
            </button>
          </div>
        </div>
      </div>
      {/* Help Card */}

      <div
        className="w-20 h-20 bg-green-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-green-300 font-bold">?</span>
      </div>


      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div
            className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}
          >
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.createLeagueHelp.title')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.createLeagueHelp.content')}
            </div>
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Progress Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 sticky top-8">
              <h2 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.progress')}</h2>
              <div className="space-y-3">
                {stepTitles.map((title, index) => {
                  const stepNumber = index + 1;
                  const isActive = stepNumber === formState.currentStep;
                  const isCompleted = stepNumber < formState.currentStep;

                  return (
                    <div
                      key={stepNumber}
                      onClick={() => goToStep(stepNumber)}
                      className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition duration-200 ${isActive
                          ? 'bg-purple-600 text-white'
                          : isCompleted
                            ? 'bg-green-600/30 text-green-200 hover:bg-green-600/40'
                            : 'bg-white/5 text-purple-200 hover:bg-white/10'
                        }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${isActive
                          ? 'bg-white text-purple-600'
                          : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-white/20 text-purple-200'
                        }`}>
                        {isCompleted ? '✓' : stepNumber}
                      </div>
                      <span className="text-sm font-medium">{title}</span>
                    </div>
                  );
                })}
              </div>

              {/* Progress Bar */}
              <div className="mt-6">
                <div className="flex justify-between text-sm text-purple-200 mb-2">
                  <span>{t('admin.createLeague.progress')}</span>
                  <span>{Math.round((formState.currentStep / formState.totalSteps) * 100)}%</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(formState.currentStep / formState.totalSteps) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
              {/* Step Content */}
              <div className="p-8">
                {renderCurrentStep()}
              </div>

              {/* Navigation Footer */}
              <div className="border-t border-white/20 px-8 py-6">
                <div className="flex justify-between">
                  <button
                    onClick={prevStep}
                    disabled={formState.currentStep === 1}
                    className="px-6 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('admin.createLeague.previous')}
                  </button>

                  <div className="text-center text-purple-200">
                    {t('admin.createLeague.stepOf', { current: formState.currentStep, total: formState.totalSteps })}
                  </div>

                  {formState.currentStep < formState.totalSteps ? (
                    <button
                      onClick={nextStep}
                      className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition duration-200"
                    >
                      {t('admin.createLeague.next')}
                    </button>
                  ) : (
                    <button
                      onClick={submitLeague}
                      disabled={formState.isSubmitting}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200 disabled:opacity-50"
                    >
                      {formState.isSubmitting ? t('admin.createLeague.creatingLeague') : t('admin.createLeague.createButton')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Error Display */}
            {formState.errors.length > 0 && (
              <div className="mt-6 bg-red-500/20 border border-red-500 rounded-lg p-4">
                <h3 className="text-red-300 font-semibold mb-2">{t('admin.createLeague.fixErrors')}</h3>
                <ul className="space-y-1">
                  {formState.errors.map((error, index) => (
                    <li key={index} className="text-red-200 text-sm">
                      • {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLeague;